import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Input,
    HStack, VStack, Image, Flex, Icon, useToast, Wrap, WrapItem, IconButton, Progress,
} from '@chakra-ui/react';
import { WarningIcon, CloseIcon } from '@chakra-ui/icons';
import { FaCamera, FaImages, FaCheckCircle } from 'react-icons/fa';
import { corrigirImagemProduto, previewSemFundoImagem } from '../../services/api';
import RevisaoFotosSemFundo from './RevisaoFotosSemFundo';
import DesbloqueioProdutoCard from './DesbloqueioProdutoCard';
import { useBloqueioProduto } from './useBloqueioProduto';
import { enviarFotosProcessador } from './enviarFotosProcessador';
import { atualizarCacheImagem } from '../ProdutoImagem';
import EditorImagemModal from '../ProdutoImagensTab/EditorImagemModal';

const MIN_FOTOS = 2;

const mensagemPublicado = (res) => {
    if (res?.desbloqueado) return 'Imagem corrigida e publicada. Produto liberado.';
    if (res?.produto_bloqueado) return 'Imagem corrigida e publicada. O produto continua bloqueado.';
    if (res?.produto_bloqueado === false) return 'Imagem corrigida e publicada.';
    return 'Imagem corrigida e publicada. Produto liberado.';
};

const CorrigirImagemModal = ({ isOpen, onClose, product, userId, onConfirmed }) => {
    const toast = useToast();
    const cameraInputRef = useRef(null);
    const galeriaInputRef = useRef(null);
    const [fotos, setFotos] = useState([]);
    const [etapa, setEtapa] = useState('fotos');
    const [previas, setPrevias] = useState([]);
    const [escolhas, setEscolhas] = useState({});
    const [processando, setProcessando] = useState({});
    const [preparando, setPreparando] = useState(false);
    const [imagemEditando, setImagemEditando] = useState(null);
    const [salvando, setSalvando] = useState(false);

    const devolucaoId = product?.devolucaoId || null;
    const revisaoPosicao = product?.revisaoPosicao || null;
    const {
        bloqueio, carregando: carregandoBloqueio, erro: erroBloqueio,
        desbloquear, setDesbloquear, consultar: consultarBloqueio, desbloquearEscolhido,
    } = useBloqueioProduto({
        ativo: isOpen,
        devolucaoId,
        produtoId: product?.id || null,
        vendaId: product?.vendaId || null,
        produtoEstoqueId: product?.produtoEstoqueId || null,
    });

    useEffect(() => {
        if (!isOpen) {
            setFotos(prev => { prev.forEach(f => URL.revokeObjectURL(f.url)); return []; });
            setEtapa('fotos');
            setPrevias([]);
            setEscolhas({});
            setProcessando({});
        }
    }, [isOpen, product?.uniqueId]);

    const handleAddFiles = (e) => {
        const novos = Array.from(e.target.files || [])
            .filter(f => f.type.startsWith('image/'))
            .map(f => ({ file: f, url: URL.createObjectURL(f) }));
        setFotos(prev => [...prev, ...novos]);
        e.target.value = '';
    };

    const handleRemoveFoto = (idx) => {
        setFotos(prev => {
            const alvo = prev[idx];
            if (alvo) URL.revokeObjectURL(alvo.url);
            return prev.filter((_, i) => i !== idx);
        });
    };

    const fotosOk = fotos.length >= MIN_FOTOS;

    const escolhaPadrao = (previa) => (previa.url_sem_fundo && !previa.alerta ? previa.caminho_sem_fundo : null);

    const handleContinuar = async () => {
        if (!fotosOk || !product) return;
        setPreparando(true);
        setPrevias(fotos.map((_, idx) => ({ chave: idx, estado: 'fila' })));
        setEscolhas({});
        setEtapa('revisao');
        try {
            const resultados = await enviarFotosProcessador({
                fotos,
                produtoId: product.id,
                onEstado: (idx, estado) => setPrevias(prev => prev.map(p => (p.chave === idx ? { ...p, estado } : p))),
                onPrevia: (idx, previa) => {
                    setPrevias(prev => prev.map(p => (p.chave === idx ? { ...previa, chave: idx, estado: 'pronto' } : p)));
                    if (previa?.imagem_id) {
                        setEscolhas(prev => ({ ...prev, [previa.imagem_id]: escolhaPadrao(previa) }));
                    }
                },
            });

            const validas = resultados.filter(r => r?.imagem_id);
            setPrevias(validas.map((previa, idx) => ({ ...previa, chave: idx, estado: 'pronto' })));

            if (validas.length < MIN_FOTOS) {
                toast({ title: 'Falha ao enviar as fotos. Tente novamente.', status: 'error', isClosable: true });
                setEtapa('fotos');
            } else if (validas.length < resultados.length) {
                const fora = resultados.length - validas.length;
                toast({
                    title: fora > 1
                        ? `${fora} fotos não subiram e ficaram de fora. Volte e envie de novo se precisar.`
                        : '1 foto não subiu e ficou de fora. Volte e envie de novo se precisar.',
                    status: 'warning',
                    position: 'top',
                    duration: 6000,
                    isClosable: true,
                });
            }
        } catch (err) {
            toast({ title: 'Erro ao preparar as fotos. Tente novamente.', status: 'error', isClosable: true });
            setEtapa('fotos');
        } finally {
            setPreparando(false);
        }
    };

    const handleEscolher = (imagemId, usarSemFundo) => {
        const previa = previas.find(p => p.imagem_id === imagemId);
        setEscolhas(prev => ({ ...prev, [imagemId]: usarSemFundo ? (previa?.caminho_sem_fundo || null) : null }));
    };

    const handleEditar = (previa) => {
        setImagemEditando({
            id: previa.imagem_id,
            url: previa.url_base_edicao || previa.url_original,
            url_original: null,
            deleted_at: null,
        });
    };

    const handleEditada = async (imagemAtualizada, { removeuFundo }) => {
        const imagemId = imagemEditando?.id;
        setImagemEditando(null);
        if (!imagemId) return;

        if (removeuFundo) {
            setPrevias(prev => prev.map(p => (p.imagem_id === imagemId
                ? { ...p, url_original: imagemAtualizada.url, url_base_edicao: imagemAtualizada.url_original || imagemAtualizada.url, url_sem_fundo: null, caminho_sem_fundo: null, alerta: null, editada: true }
                : p)));
            setEscolhas(prev => ({ ...prev, [imagemId]: null }));
            return;
        }

        setProcessando(prev => ({ ...prev, [imagemId]: true }));
        try {
            const previa = await previewSemFundoImagem({ imagem_id: imagemId });
            if (previa?.error) {
                toast({ title: previa.error, status: 'error', isClosable: true });
                return;
            }
            setPrevias(prev => prev.map(p => (p.imagem_id === imagemId ? { ...previa, chave: p.chave, estado: 'pronto', editada: true } : p)));
            setEscolhas(prev => ({ ...prev, [imagemId]: escolhaPadrao(previa) }));
        } finally {
            setProcessando(prev => ({ ...prev, [imagemId]: false }));
        }
    };

    const handleTentarOutroModo = async (imagemId, modelo) => {
        setProcessando(prev => ({ ...prev, [imagemId]: true }));
        try {
            const previa = await previewSemFundoImagem({ imagem_id: imagemId, modelo });
            if (previa?.error) {
                toast({ title: previa.error, status: 'error', isClosable: true });
                return;
            }
            setPrevias(prev => prev.map(p => (p.imagem_id === imagemId ? { ...previa, chave: p.chave, estado: 'pronto' } : p)));
            setEscolhas(prev => ({ ...prev, [imagemId]: escolhaPadrao(previa) }));
        } finally {
            setProcessando(prev => ({ ...prev, [imagemId]: false }));
        }
    };

    const handlePublicar = async () => {
        const publicaveis = previas.filter(p => p.imagem_id);
        if (!product || preparando || carregandoBloqueio || publicaveis.length < MIN_FOTOS) return;
        setSalvando(true);
        try {
            const semFundo = {};
            Object.entries(escolhas).forEach(([imagemId, caminho]) => {
                if (caminho) semFundo[imagemId] = caminho;
            });

            const payload = {
                produto_id: product.id,
                imagem_ids: publicaveis.map(p => p.imagem_id),
                sem_fundo: semFundo,
                desbloquear: desbloquearEscolhido,
                userId,
            };

            if (devolucaoId) {
                payload.devolucao_id = devolucaoId;
                payload.observacao = product.observacao || null;
            } else if (revisaoPosicao) {
                payload.revisao_posicao = revisaoPosicao;
            } else {
                payload.venda_id = product.vendaId;
                payload.produto_estoque_id = product.produtoEstoqueId;
            }

            const res = await corrigirImagemProduto(payload);

            if (res?.success) {
                toast({ title: mensagemPublicado(res), status: 'success', duration: 3000, isClosable: true });
                if (res.aviso) {
                    toast({ title: res.aviso, status: 'warning', duration: 8000, isClosable: true });
                }
                atualizarCacheImagem(product.id, null);
                if (onConfirmed) onConfirmed(product, res);
                onClose();
            } else {
                toast({ title: res?.error || 'Erro ao corrigir imagem', status: 'error', isClosable: true });
            }
        } catch (err) {
            toast({ title: 'Erro ao processar. Tente novamente.', status: 'error', isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    if (!product) return null;

    const ocupado = preparando || salvando;
    const prontas = previas.filter(p => p.estado === 'pronto' || (!p.estado && p.imagem_id)).length;

    const cardBloqueio = (
        <DesbloqueioProdutoCard
            carregando={carregandoBloqueio}
            erro={erroBloqueio}
            bloqueio={bloqueio}
            desbloquear={desbloquear}
            onChange={setDesbloquear}
            onTentarNovamente={consultarBloqueio}
            desabilitado={salvando}
        />
    );

    return (
        <>
        <Modal isOpen={isOpen} onClose={ocupado ? () => {} : onClose} isCentered size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent mx={{ base: 0, md: 4 }}>
                <ModalHeader fontSize={{ base: 'sm', md: 'md' }} pr={10} wordBreak="break-word">
                    {etapa === 'fotos'
                        ? 'Corrigir imagem do produto'
                        : (preparando ? `Preparando fotos (${prontas} de ${previas.length})` : 'Confira antes de publicar')}
                    {preparando && (
                        <Progress
                            mt={2}
                            size="xs"
                            colorScheme="purple"
                            borderRadius="full"
                            value={previas.length ? (prontas / previas.length) * 100 : 0}
                            hasStripe
                            isAnimated
                        />
                    )}
                </ModalHeader>
                {!ocupado && <ModalCloseButton />}
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Box p={3} bg="purple.50" borderRadius="md" borderWidth="1px" borderColor="purple.200">
                            <Text fontWeight="semibold" fontSize="sm">{product.name}</Text>
                            {product.globalCode && (
                                <Text fontSize="xs" color="gray.600">Global: {product.globalCode}</Text>
                            )}
                            {product.brand && (
                                <Text fontSize="xs" color="gray.600">Marca: {product.brand}</Text>
                            )}
                            <Text fontSize="xs" color="purple.700" mt={1}>
                                As fotos viram a imagem oficial do produto e as imagens antigas são bloqueadas. {revisaoPosicao ? 'Não movimenta estoque e não altera a contagem.' : 'Não movimenta estoque.'}
                            </Text>
                        </Box>

                        {etapa === 'fotos' && cardBloqueio}

                        {etapa === 'fotos' ? (
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Fotos do item físico (mín. {MIN_FOTOS})</FormLabel>
                                <Input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple display="none" onChange={handleAddFiles} />
                                <Input ref={galeriaInputRef} type="file" accept="image/*" multiple display="none" onChange={handleAddFiles} />
                                <HStack spacing={2}>
                                    <Button size="sm" leftIcon={<Icon as={FaCamera} />} onClick={() => cameraInputRef.current?.click()} colorScheme="purple" variant="outline">
                                        Tirar foto
                                    </Button>
                                    <Button size="sm" leftIcon={<Icon as={FaImages} />} onClick={() => galeriaInputRef.current?.click()} variant="outline">
                                        Da galeria
                                    </Button>
                                </HStack>

                                {fotos.length > 0 && (
                                    <Wrap mt={3} spacing={2}>
                                        {fotos.map((f, idx) => (
                                            <WrapItem key={idx} position="relative">
                                                <Image src={f.url} alt={`Foto ${idx + 1}`} boxSize="64px" objectFit="cover" borderRadius="md" borderWidth="1px" borderColor="gray.200" />
                                                <IconButton aria-label="Remover foto" icon={<CloseIcon boxSize={2} />} size="xs" colorScheme="red" borderRadius="full" position="absolute" top="-6px" right="-6px" onClick={() => handleRemoveFoto(idx)} />
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                )}

                                {!fotosOk && (
                                    <Flex align="center" gap={2} mt={2} color="red.600">
                                        <WarningIcon boxSize={3} />
                                        <Text fontSize="xs">Adicione ao menos {MIN_FOTOS} fotos de ângulos diferentes.</Text>
                                    </Flex>
                                )}
                            </FormControl>
                        ) : (
                            <RevisaoFotosSemFundo
                                previas={previas}
                                escolhas={escolhas}
                                processando={processando}
                                onEscolher={handleEscolher}
                                onTentarOutroModo={handleTentarOutroModo}
                                onEditar={handleEditar}
                            />
                        )}

                        {etapa === 'revisao' && cardBloqueio}
                    </VStack>
                </ModalBody>
                <ModalFooter px={{ base: 3, md: 6 }}>
                    <Flex w="100%" gap={2} flexWrap="wrap" justify={{ base: 'stretch', md: 'flex-end' }}>
                        {etapa === 'revisao' && (
                            <Button variant="ghost" onClick={() => setEtapa('fotos')} isDisabled={ocupado} flex={{ base: '1 1 auto', md: '0 0 auto' }}>Voltar</Button>
                        )}
                        <Button variant="ghost" onClick={onClose} isDisabled={ocupado} flex={{ base: '1 1 auto', md: '0 0 auto' }}>Cancelar</Button>
                        {etapa === 'fotos' ? (
                            <Button colorScheme="purple" onClick={handleContinuar} isLoading={preparando} loadingText="Preparando" isDisabled={!fotosOk || ocupado} flex={{ base: '1 1 auto', md: '0 0 auto' }}>
                                Continuar
                            </Button>
                        ) : (
                            <Button colorScheme="purple" leftIcon={<Icon as={FaCheckCircle} />} onClick={handlePublicar} isLoading={salvando} isDisabled={ocupado || carregandoBloqueio} flex={{ base: '1 1 auto', md: '0 0 auto' }}>
                                Publicar imagem
                            </Button>
                        )}
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <EditorImagemModal
            isOpen={!!imagemEditando}
            imagem={imagemEditando}
            onClose={() => setImagemEditando(null)}
            onSalvo={handleEditada}
        />
        </>
    );
};

export default CorrigirImagemModal;
